import React, { Suspense, lazy, useState, useCallback, useContext, createContext, useRef } from 'react';

const ConfirmDialogView = lazy(() => import('./ConfirmDialogView'));

/**
 * ConfirmDialog — branded replacement for window.confirm().
 *
 * Usage (simple):
 *   const confirm = useConfirm();
 *   if (await confirm({ title: 'Delete event?', body: '...' })) { ... }
 *
 * Returns a Promise<boolean>. The provider must wrap the app once.
 *
 * Built on shadcn AlertDialog so it inherits theme + accessibility.
 */

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: 'Are you sure?',
  body: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  destructive: false,
};

export const ConfirmProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(DEFAULTS);
  const resolverRef = useRef(null);

  const confirm = useCallback((nextOpts) => {
    setOpts({ ...DEFAULTS, ...nextOpts });
    setOpen(true);
    return new Promise((resolve) => { resolverRef.current = resolve; });
  }, []);

  const handle = (result) => {
    setOpen(false);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {open && <Suspense fallback={null}><ConfirmDialogView open={open} opts={opts} onResult={handle} /></Suspense>}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
};

export default ConfirmProvider;
